import { useCallback, useState } from 'react';

import type {
	FeedbackModalProps,
	FeedbackHandler,
	ContentFeedbackService,
} from '../../entry-points/content-types-feedback-service.ts';
import { Deferrable } from '../utils/deferrable.ts';
import type { GlobalFeedbackCollectorComponentProps } from '@confluence/global-feedback-collector/entry-points/GlobalFeedbackCollector.ts';

type GlobalFeedbackCollectorModalState =
	| {
			isModalOpen: false;
	  }
	| {
			isModalOpen: true;
			modalProps: FeedbackModalProps;
			modalCloseDeferrable: Deferrable<void>;
	  };

export const useGlobalFeedbackCollector = (
	feedbackService: ContentFeedbackService,
):
	| {
			isModalOpen: false;
	  }
	| {
			isModalOpen: true;
			modalProps: GlobalFeedbackCollectorComponentProps;
			closeModal: () => void;
	  } => {
	const [modalState, setModalState] = useState<GlobalFeedbackCollectorModalState>({
		isModalOpen: false,
	});

	const closeModal = useCallback(() => {
		if (modalState.isModalOpen) {
			modalState.modalCloseDeferrable.resolve();
		}
		setModalState({ isModalOpen: false });
	}, [modalState]);

	const feedbackHandler: FeedbackHandler = useCallback((modalProps: FeedbackModalProps) => {
		const deferrable = new Deferrable<void>();

		setModalState({
			isModalOpen: true,
			modalProps,
			modalCloseDeferrable: deferrable,
		});

		return deferrable.get();
	}, []);

	feedbackService.registerFeedbackHandler(feedbackHandler);
	feedbackService.registerCloseHandler(closeModal);

	return modalState.isModalOpen
		? {
				isModalOpen: modalState.isModalOpen,
				modalProps: modalState.modalProps,
				closeModal,
			}
		: modalState;
};
